import { Table } from "@4uhub/lib4uhub";
import { Chip } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../store/store";
import CellData from "../../components/UI/CellData/CellData";
import { IRelationShipScreenProps } from "./models";
import { genericMemo } from "../../components/SubFormArray/genericMemo";
import RelationShipFilter from "./RelationShipFilter";

const translationPath = "page.4uBus.relationships.defaultRelationships.";

export const RelationShipScreen = genericMemo(
  <T extends object>({
    isTag,
    customProps,
    service,
    customHandleAdd,
    customHandleRowClick,
  }: IRelationShipScreenProps<T>) => {
    const [filterParams, setFilterParams] = useState<
      Record<string, any> | undefined
    >();

    const currentPage = useAppSelector((state) => state.menu.actualPage?.name);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const { extension } = useParams();

    const columns: GridColDef<T>[] = useMemo(() => {
      const initialValues: GridColDef<T>[] = [];
      const baseValues: GridColDef<T>[] = [
        {
          field: "default",
          headerName: t(translationPath + "default") || "Default",
          maxWidth: 150,
          renderCell: (params) => {
            return (
              <Chip
                size="small"
                label={
                  params.value
                    ? t(translationPath + "is_default")
                    : t(translationPath + "is_not_default")
                }
                color={params.value ? "success" : "default"}
              />
            );
          },
        },
        {
          field: "enable",
          headerName: t(translationPath + "status") || "Status",
          maxWidth: 150,
          renderCell: (params) => {
            return (
              <Chip
                size="small"
                label={
                  params.value
                    ? t(translationPath + "active")
                    : t(translationPath + "inactive")
                }
                color={params.value ? "success" : "error"}
              />
            );
          },
        },
      ];

      if (isTag) {
        initialValues.push(
          {
            field: "_4uBusRelationshipType",
            headerName:
              t(translationPath + "relationship_type") || "Relationship Type",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value.name} />;
              }
            },
          },
          {
            field: "originName",
            headerName: t(translationPath + "origin_name") || "Origin name",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value} />;
              }
            },
          },
          {
            field: "originValue",
            headerName: t(translationPath + "origin_value") || "Origin value",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value} />;
              }
            },
          },
          {
            field: "destinityName",
            headerName: t(translationPath + "destiny_name") || "Destiny name",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value} />;
              }
            },
          },
          {
            field: "destinityValue",
            headerName: t(translationPath + "destiny_value") || "Destiny value",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value} />;
              }
            },
          },
        );
      } else {
        initialValues.push(
          {
            field: "setRelationshipType",
            headerName:
              t(translationPath + "relationship_type") || "Relationship Type",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value.name} />;
              }
            },
          },
          {
            field: "directionType",
            headerName:
              t(translationPath + "direction_type") || "Direction Type",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value.name} />;
              }
            },
          },
          {
            field: "originName",
            headerName: t(translationPath + "origin_name") || "Origin name",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value} />;
              }
            },
          },
          {
            field: "originValue",
            headerName: t(translationPath + "origin_value") || "Origin value",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value} />;
              }
            },
          },
          {
            field: "destinyName",
            headerName: t(translationPath + "destiny_name") || "Destiny name",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value} />;
              }
            },
          },
          {
            field: "destinyValue",
            headerName: t(translationPath + "destiny_value") || "Destiny value",
            renderCell: (params) => {
              if (!params.value) {
                return "-";
              } else {
                return <CellData value={params.value} />;
              }
            },
          },
        );
      }

      return [...initialValues, ...baseValues];
    }, [t, isTag]);

    const handleAdd = () => {
      navigate("new");
    };

    const handleRowClick = (params: GridRowParams<T>) => {
      navigate(`${params.id.toString()}/edit`);
    };

    const props = useMemo(() => {
      return {
        ExtensionCode: extension,
        ...customProps,
        ...filterParams,
      };
    }, [extension, filterParams, customProps]);

    return (
      <Table<T>
        showDefaultMacroFilters={false}
        service={service}
        columns={columns}
        title={
          isTag
            ? ""
            : currentPage
              ? t(currentPage)
              : t(translationPath + "relationship")
        }
        searchInput
        onAdd={customHandleAdd ?? handleAdd}
        onRowClick={customHandleRowClick ?? handleRowClick}
        serviceProps={isTag ? customProps : props}
        searchInputPlaceHolder={
          t(translationPath + "search") || "Search for a relationship"
        }
        addButtonLabel={
          t(translationPath + "add_relationship") || "Add Relationship"
        }
        macroFilters={() => (
          <RelationShipFilter
            filterParams={filterParams}
            setFilterParams={setFilterParams}
          />
        )}
        confirmOptions={{
          title: t(translationPath + "confirm.title"),
          description: t(translationPath + "confirm.description"),
          confirmationButtonProps: {
            variant: "contained",
          },
        }}
      />
    );
  },
);
